"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createBasicGraphUtils = exports.splitDisjointGraphs = void 0;
const common_1 = require("@finley-technologies/common");
// this file should only expose generic graph utils.
// must not include any artifact type specific logic.
// that is do not include file below.
// import * as dbNodes from '../dbNodes/dbNodes';
/**
 * Splits a set of artifacts into disjoint connected graphs (connected
 * components), treating dependencies as undirected edges. A ConnectedGraph is an
 * array of artifactIds that are reachable from one another.
 *
 * This is the pure, record-free core of {@link IBasicGraphUtils.splitDisjointGraphs}
 * — it needs only artifact ids and dependency edges, so callers that only have
 * lightweight artifact rows (e.g. metadata paths) can reuse the exact same graph
 * logic instead of reimplementing it.
 */
const splitDisjointGraphs = ({ artifactIds, dependencies, }) => {
    const neighborsByArtifactId = new Map();
    const addEdge = (from, to) => {
        const neighbors = neighborsByArtifactId.get(from);
        if (neighbors) {
            neighbors.push(to);
        }
        else {
            neighborsByArtifactId.set(from, [to]);
        }
    };
    dependencies.forEach(({ inputArtifactId, outputArtifactId }) => {
        addEdge(inputArtifactId, outputArtifactId);
        addEdge(outputArtifactId, inputArtifactId);
    });
    const graphs = [];
    const visited = new Set();
    const dfs = (node, graph) => {
        if (!visited.has(node)) {
            visited.add(node);
            graph.push(node);
            (neighborsByArtifactId.get(node) ?? []).forEach((connection) => {
                dfs(connection, graph);
            });
        }
        return graph;
    };
    artifactIds.forEach((id) => {
        if (!visited.has(id)) {
            graphs.push(dfs(id, []));
        }
    });
    return graphs;
};
exports.splitDisjointGraphs = splitDisjointGraphs;
const createBasicGraphUtils = ({ artifactDocumentId, version, artifacts, cfIdToSlugMap, lpIdToSlugMap, dependencies, }) => {
    const prefix = `[artifactDocumentId:${artifactDocumentId}]`;
    // The id/slug maps only contain credit facilities / loan products that some artifact in
    // *this* pipeline graph references. A miss almost always means a setup-ordering gap: the
    // facility / loan product was added to the credit agreement before it was configured in
    // the tape-builder graph. This is expected during facility onboarding until graph setup is
    // finished — the message guides the user to complete that step rather than implying a bug.
    const notInGraphMessage = (label, value) => `${prefix}: [${label}:${value}] is not yet configured in this pipeline's tape-builder graph, ` +
        `so its credit-agreement config (loan tape / borrowing-base mechanics) cannot be resolved. ` +
        `Credit facilities and loan products must be added to the tape-builder graph before their ` +
        `credit-agreement config can be viewed or applied. This usually means the facility/loan product ` +
        `was added to the credit agreement but its graph setup is incomplete — finish configuring it in ` +
        `the graph, then retry.`;
    // generate mapping of artifactId to artifact.
    const artifactIdToArtifact = new Map(artifacts.map((artifact) => [artifact.id, artifact]));
    // assert that all artifactIds are unique.
    (0, common_1.finleyAssert)(artifactIdToArtifact.size === artifacts.length, `${prefix}: artifactIds are not
    unique`);
    const getArtifactById = (artifactId) => {
        const found = artifactIdToArtifact.get(artifactId);
        (0, common_1.finleyAssert)(found, `${prefix}: [artifactId:${artifactId}] not found`);
        return found;
    };
    // Direct neighbours per artifact, in dependency-row order (duplicates kept).
    const outputsById = new Map();
    const inputsById = new Map();
    const addNeighbour = (index, id, neighbour) => {
        const neighbours = index.get(id);
        if (neighbours) {
            neighbours.push(neighbour);
        }
        else {
            index.set(id, [neighbour]);
        }
    };
    dependencies.forEach(({ inputArtifactId, outputArtifactId }) => {
        addNeighbour(outputsById, inputArtifactId, outputArtifactId);
        addNeighbour(inputsById, outputArtifactId, inputArtifactId);
    });
    // returns direct outputs of the given artifactId
    const getOutputs = (artifactId) => [
        ...(outputsById.get(artifactId) ?? []),
    ];
    // returns direct inputs of the given artifactId
    const getInputs = (artifactId) => [
        ...(inputsById.get(artifactId) ?? []),
    ];
    // Memoizes `collect` per artifact within one top-level call, so a diamond
    // is walked once instead of once per path. Results (and their order) match
    // the plain recursion; a cycle fails an assertion instead of overflowing.
    const recurse = (artifactId, collect, direction) => {
        const memo = new Map();
        const inProgress = new Set();
        const visit = (id) => {
            const cached = memo.get(id);
            if (cached) {
                return cached;
            }
            (0, common_1.finleyAssert)(!inProgress.has(id), `${prefix}: cycle found while collecting recursive ${direction} of [artifactId:${id}]`);
            inProgress.add(id);
            const result = collect(id, visit);
            inProgress.delete(id);
            memo.set(id, result);
            return result;
        };
        return [...visit(artifactId)];
    };
    // returns connections to the given artifactId
    const getDirectConnections = (artifactId) => {
        return dependencies
            .filter(({ inputArtifactId, outputArtifactId }) => inputArtifactId === artifactId || outputArtifactId === artifactId)
            .map(({ inputArtifactId, outputArtifactId }) => inputArtifactId === artifactId ? outputArtifactId : inputArtifactId);
    };
    // returns all outputs of the given artifactId
    const getRecursiveOutputs = (artifactId) => recurse(artifactId, (id, visit) => {
        const directDependents = getOutputs(id);
        const recursiveDependents = directDependents.flatMap(visit);
        return Array.from(new Set([...recursiveDependents, ...directDependents]));
    }, 'outputs');
    // returns all inputs for the given artifactId
    const getRecursiveInputs = (artifactId) => recurse(artifactId, (id, visit) => {
        const directInputs = getInputs(id);
        const recursiveInputs = directInputs.flatMap(visit);
        return Array.from(new Set([...directInputs, ...recursiveInputs]));
    }, 'inputs');
    const hasArtifact = ({ artifactId }) => {
        return artifactIdToArtifact.has(artifactId);
    };
    const getArtifactSlug = ({ artifactId }) => {
        return getArtifactById(artifactId).artifactSlug;
    };
    const getOutputArtifacts = (artifactId) => getOutputs(artifactId).map(getArtifactById);
    const getInputArtifacts = (artifactId) => getInputs(artifactId).map(getArtifactById);
    const cfIdToSlug = (id) => {
        const slug = cfIdToSlugMap.get(id);
        (0, common_1.finleyAssert)(slug, notInGraphMessage('creditFacilityId', id));
        return slug;
    };
    const getCFSlugs = () => Array.from(cfIdToSlugMap.values());
    const getCFIds = () => Array.from(cfIdToSlugMap.keys());
    const getLPSlugs = () => Array.from(lpIdToSlugMap.values());
    const getLPIds = () => Array.from(lpIdToSlugMap.keys());
    const lpIdToSlug = (id) => {
        const slug = lpIdToSlugMap.get(id);
        (0, common_1.finleyAssert)(slug, notInGraphMessage('loanProductId', id));
        return slug;
    };
    const lpSlugToId = (slug) => {
        const id = Array.from(lpIdToSlugMap.entries()).find(([, v]) => v === slug)?.[0];
        (0, common_1.finleyAssert)(id, notInGraphMessage('loanProductSlug', slug));
        return id;
    };
    const cfSlugToId = (slug) => {
        const id = Array.from(cfIdToSlugMap.entries()).find(([, v]) => v === slug)?.[0];
        (0, common_1.finleyAssert)(id, notInGraphMessage('creditFacilitySlug', slug));
        return id;
    };
    /**
     * ArtifactWithSlugs => Artifact
     */
    const toArtifact = (artifactWithSlugs) => {
        const { creditFacilitySlug, loanProductSlug, ...rest } = artifactWithSlugs;
        const creditFacilityId = creditFacilitySlug
            ? cfSlugToId(creditFacilitySlug)
            : null;
        const loanProductId = loanProductSlug ? lpSlugToId(loanProductSlug) : null;
        return {
            ...rest,
            creditFacilityId,
            loanProductId,
        };
    };
    // walk all the dependencies and split the graph into
    // ConnectedGraph - which is an array of artifactIds that are connected.
    // Delegates to the pure, record-free implementation so there is a single
    // source of truth for connected-component logic.
    const splitDisjointGraphsForDocument = () => (0, exports.splitDisjointGraphs)({
        artifactIds: artifacts.map(({ id }) => id),
        dependencies,
    });
    const bfsTraversal = ({ startNodes, criteria, search = 'outputs', }) => {
        const visitedNodes = new Set();
        const queue = [...startNodes];
        const matchingNodes = [];
        // Iterate through the queue until it's empty
        // eslint-disable-next-line no-restricted-syntax
        while (queue.length > 0) {
            const currentNode = queue.shift();
            (0, common_1.finleyAssert)(currentNode, `BFS error: queue is empty unexpectedly`);
            if (visitedNodes.has(currentNode)) {
                continue; // Skip if we've already visited this node
            }
            visitedNodes.add(currentNode);
            const { matches, continueBranch } = criteria(currentNode);
            if (matches) {
                matchingNodes.push(currentNode);
            }
            if (!continueBranch) {
                continue; // Skip adding descendants if we're not continuing in this branch
            }
            const connections = [];
            switch (search) {
                case 'outputs':
                    connections.push(...getOutputs(currentNode));
                    break;
                case 'inputs':
                    connections.push(...getInputs(currentNode));
                    break;
                case 'both':
                    connections.push(...getOutputs(currentNode), ...getInputs(currentNode));
                    break;
                default:
                    (0, common_1.finleyAssertUnreachable)(search);
            }
            // Add unvisited connections to the queue
            connections.forEach((connectedNode) => {
                if (!visitedNodes.has(connectedNode)) {
                    queue.push(connectedNode);
                }
            });
        }
        return matchingNodes;
    };
    return {
        getArtifactDocumentId: () => artifactDocumentId,
        getArtifactDocumentVersion: () => version,
        getAllArtifacts: () => artifacts,
        cfIdToSlug,
        cfSlugToId,
        lpIdToSlug,
        lpSlugToId,
        getCFSlugs,
        getCFIds,
        getLPSlugs,
        getLPIds,
        hasArtifact,
        getArtifactSlug,
        getOutputs,
        getInputs,
        bfsTraversal,
        getDirectConnections,
        getRecursiveOutputs,
        getRecursiveInputs,
        splitDisjointGraphs: splitDisjointGraphsForDocument,
        getArtifactById,
        getInputArtifacts,
        getOutputArtifacts,
        toArtifact,
    };
};
exports.createBasicGraphUtils = createBasicGraphUtils;
//# sourceMappingURL=dbBasicGraphUtils.js.map